import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Plain-language explanation of every Primary Trigger label the scoring engine can emit
// (mirrors backend SIGNAL_CATALOG / SECURITY_CATALOG labels + special manual/seed values).
const TRIGGER_DESCRIPTIONS: Record<string, string> = {
  "Active Defence work package or EOI": "An open Defence work package or Expression of Interest — a strong, current indicator of active Defence demand.",
  "Defence contract": "Evidence of an awarded or in-progress Defence contract.",
  "Named Defence prime relationship": "A named working relationship with a Defence prime contractor.",
  "Explicit Defence growth strategy": "The company has publicly stated a deliberate strategy to grow in Defence.",
  "AUKUS involvement or opportunity": "Involvement in, or a stated opportunity tied to, the AUKUS programme.",
  "Defence expo / industry participation": "Participation in Defence expos or industry events.",
  "Confirmed DIDG recipient": "Confirmed recipient of a Defence Industry Development Grant (DIDG).",
  "Generic Defence capability statement": "A general capability statement referencing Defence — a weaker, non-specific signal.",
  "Named opportunity in Public AIC Plan": "Named in a published Australian Industry Capability (AIC) Plan opportunity.",
  "New Defence division / capability": "The company has stood up a new Defence-focused division or capability.",
  "Recruiting Defence-specific roles": "Actively recruiting for Defence-specific roles — a timely 'why now' hiring signal.",
  "Recruiting security roles": "Actively recruiting security or clearance-related roles.",
  "Recent AS9100 certification": "Recently achieved AS9100 aerospace/defence quality certification.",
  "New Australian manufacturing facility": "Established a new Australian manufacturing facility.",
  "Prospect-confirmed DISP/E8/security interest": "The prospect has confirmed interest in DISP membership, Essential Eight uplift or broader security maturity.",
  "Contract/tender/prime security obligation": "A contract, tender or prime relationship imposes a security obligation on the company.",
  "Assessment-identified uplift area": "A security assessment has identified a specific uplift area.",
  "Prospect-confirmed implementation/governance need": "The prospect has confirmed a security implementation or governance need.",
  "New Defence capability + confirmed security uplift need": "Combines a newly stood-up Defence capability with a confirmed security uplift need.",
  "Strong signals pending human review approval": "The company scores in the P1 band on its signals, but the lead cannot be marked review-ready until an analyst approves it and evidence confidence is high enough.",
  "Manual candidate entry": "Added manually by an analyst; no automated trigger has been derived yet.",
  "Active Defence work package or EOI ": "An open Defence work package or Expression of Interest.",
};

export function triggerInfo(label?: string | null): { label: string; description: string } {
  if (!label) return { label: "—", description: "" };
  const description = TRIGGER_DESCRIPTIONS[label] || "The strongest current signal driving this company's priority.";
  return { label, description };
}

export function TriggerLabel({ value, testid, className }: { value?: string | null; testid?: string; className?: string }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const info = triggerInfo(value);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid={testid} onClick={(e) => e.stopPropagation()}
            className={className || "block max-w-[200px] truncate cursor-help"}>{value}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-slate-900 text-slate-50 leading-relaxed" data-testid={testid ? `${testid}-tooltip` : undefined}>
          <div className="font-semibold">{info.label}</div>
          <div className="text-slate-300 mt-1 text-xs leading-relaxed">{info.description}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
